import React, { useEffect, useMemo } from 'react';
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import GlobalDialog from '../../../../../components/mui/Dialogue/GlobalDialog';
import { CancelButton, SubmitButton } from '../../../../../components/mui/Buttons/Buttons';
import { assignCros, getCroList } from '../../../../../redux/features/crmSlice';

/**
 * Hands one student from the inactive CRO to an active one.
 *
 * The CRO options come from crm-leads/get-cro-list, which returns the ACTIVE
 * employees under the logged in CRM's reporting tree - so the dropdown is
 * already "active CROs under him" without any extra filtering here.
 *
 * Posts to crm-leads/assign with retention: true, which makes the backend
 * record the move in retention_assignment inside the same transaction as the
 * assignment, so the receiving CRO sees it under Retention Leads.
 */
const AssignCroDialog = ({ open, setOpen, row, onSuccess }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const croList = useSelector((state) => state.crm.croList);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      course_enrollment_id: row?.course_enrollment_id ?? '',
      assigned: '',
      remarks: '',
      retention: true,
    },
    validationSchema: Yup.object().shape({
      assigned: Yup.string().required('Please select a CRO'),
    }),
    onSubmit: async (values) => {
      const res = await dispatch(assignCros(values));
      if (res?.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message || 'Retention lead assigned', { variant: 'success' });
        formik.resetForm();
        setOpen(false);
        onSuccess?.();
      } else {
        enqueueSnackbar(res?.payload?.message || 'Could not assign the CRO', { variant: 'error' });
      }
    },
  });

  //MAPS THE STORED userID BACK TO ITS OPTION OBJECT . null ( NOT undefined ) SO MUI STAYS CONTROLLED
  const selectedCro = useMemo(
    () => (Array.isArray(croList) ? croList.find((cro) => cro?.userID === formik.values.assigned) ?? null : null),
    [croList, formik.values.assigned],
  );

  //THE LIST IS SMALL AND CHANGES RARELY , SO FETCH IT WHEN THE DIALOG OPENS RATHER THAN ON EVERY ROW
  useEffect(() => {
    if (open) dispatch(getCroList({ search: '' }));
  }, [open, dispatch]);

  return (
    <GlobalDialog
      title={'Assign CRO (Retention Lead)'}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton={true}
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Stack gap={2}>
              <Stack gap={0.5}>
                <Typography variant="body1">
                  <b>{row?.student_name || '—'}</b>
                  {row?.admn_no ? ` (${row.admn_no})` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Course: {row?.course_name || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current owner: {row?.owner || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Student status: {row?.course_status || '—'}
                </Typography>
              </Stack>

              <Autocomplete
                options={Array.isArray(croList) ? croList : []}
                //CONTROLLED OFF FORMIK . THE DIALOG STAYS MOUNTED BETWEEN OPENS , SO AN UNCONTROLLED
                //AUTOCOMPLETE WOULD KEEP DISPLAYING THE LAST PICKED CRO AFTER resetForm() AND WHEN
                //REOPENED ON A DIFFERENT STUDENT.
                value={selectedCro}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(opt, val) => opt.userID === val.userID}
                onChange={(e, value) => formik.setFieldValue('assigned', value?.userID || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign to CRO"
                    placeholder="Choose an active CRO"
                    error={formik.touched.assigned && Boolean(formik.errors.assigned)}
                    helperText={formik.touched.assigned && formik.errors.assigned}
                  />
                )}
              />

              <TextField
                name="remarks"
                label="Remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                fullWidth
                multiline
                rows={2}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Stack direction={'row'} spacing={1}>
                  <CancelButton size="small" action={() => setOpen(false)}>
                    Cancel
                  </CancelButton>
                  {/* NOT GATED ON state.crm.loading: getCroList has no rejected case, so a failed
                      dropdown fetch would leave that flag true and the button dead */}
                  <SubmitButton size="small" type="submit" disabled={formik.isSubmitting}>
                    {formik.isSubmitting ? 'Please wait...' : 'Assign'}
                  </SubmitButton>
                </Stack>
              </Box>
            </Stack>
          </Form>
        </FormikProvider>
      }
    />
  );
};

export default React.memo(AssignCroDialog);
